import React, { useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import { Col, Row, Card } from "react-bootstrap";
import { useSelector } from "react-redux";
import Calendar from "components/common/Calendar";
import moment from "moment";

const ClaimPayerServices = ({ show, row }) => {
  const { viewClaimInfo } = useSelector((state) => state.claim);

  const diagnosis = viewClaimInfo?.payload_json?.diag || {};
  const diagnosisList = Object.values(diagnosis);

  const getStatusInfo = (status) => {
    let statusValue = "";
    let statusStyle = { backgroundColor: "#f8f9fa", color: "#000" };

    if (status) {
      statusValue = String(status).toLowerCase();
    } else {
      statusValue = "";
    }

    // Map status to corresponding styles
    const statusStyles = {
      error: { backgroundColor: "#fee2e2", color: "#dc2626" },
      rejected: { backgroundColor: "#fee2e2", color: "#dc2626" },
      noresp: { backgroundColor: "#e5e7eb", color: "#374151" },
      approved: { backgroundColor: "#dcfce7", color: "#16a34a" },
      complete: { backgroundColor: "#dcfce7", color: "#16a34a" },
      cancelled: { backgroundColor: "#fce7f6", color: "#db2777" },
      pended: { backgroundColor: "#dbeafe", color: "#2563eb" },
      pending: { backgroundColor: "#dbeafe", color: "#2563eb" },
      queued: { backgroundColor: "#fef9c3", color: "#ca8a04" },
      partial: { backgroundColor: "#cffafe", color: "#0891b2" },
      adjudication: { backgroundColor: "#ede9fe", color: "#7c3aed" },
      "not-required": { backgroundColor: "#e0f2fe", color: "#0284c7" },
    };

    if (statusValue && statusStyles[statusValue]) {
      statusStyle = statusStyles[statusValue];
    }

    return { statusValue, statusStyle };
  };

  // Render status badge using getStatusInfo
  const renderStatusBadge = useCallback((status) => {
    const { statusValue, statusStyle } = getStatusInfo(status);
    return (
      <div
        className="d-inline-block rounded-pill px-2 py-1"
        style={{
          backgroundColor: statusStyle.backgroundColor,
          color: statusStyle.color,
          fontSize: "0.75rem",
          fontWeight: 500,
          border: "1px solid transparent",
          borderColor: "rgba(0,0,0,0.1)",
        }}
      >
        {statusValue.toUpperCase()}
      </div>
    );
  }, []);
  const formatServiceDate = (dateStr) => {
    if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr))
      return { day: "", month: "", year: "" };

    const date = moment(dateStr, "YYYY-MM-DD");
    if (!date.isValid()) return { day: "", month: "", year: "" };

    console.log({ date: date.format("DD") });

    return {
      day: date.format("DD"),
      month: date.format("MMM"),
      year: date.format("YYYY"),
    };
  };
  // Render service items
  const renderServiceItems = useMemo(() => {
    const serviceProcedures = viewClaimInfo?.service_procedures || [];
    if (!serviceProcedures?.length) return null;

    return serviceProcedures.map((item, index) => {
      const { day, month, year } = formatServiceDate(item.dateserv);

      const medReq = item.medication_request || {};
      const dosage = medReq.dosage_instruction || {};

      const hasPrescriptionDetails = Boolean(
        item.pres_med ||
        item.medication_request ||
        item.pselrsn ||
        item.pharslrsn ||
        item.pharsubs ||
        item.strngth ||
        item.pharstrnth ||
        item.pharsubsrsn ||
        item.pharsubs_oth ||
        item?.absenceRsn ||
        item?.pharscabrsn ||
        item?.pharscabrsn_oth ||
        item?.duration ||
        dosage?.duration ||
        item?.frequency ||
        dosage?.frequency ||
        item?.dose ||
        dosage?.dose ||
        item?.dosage_instruction ||
        dosage?.text ||
        item?.refill_count ||
        dosage?.refill ||
        item?.pat_inst ||
        dosage?.patient_instruction ||
        item?.time_inst_disp ||
        dosage?.time_instruction_desc ||
        item?.ROA_disp ||
        dosage?.route_desc
      );

      const durationDisplay =
        dosage.duration ||
        (item.duration
          ? `${item.duration.dur_time || ""} ${item.duration.dur_time_value || ""
          }`
          : "");
      const frequencyDisplay =
        dosage.frequency ||
        (item.frequency
          ? `${item.frequency.frq || ""} - ${item.frequency.frq_prd || "0"} ${item.frequency.frq_time_value || ""
          }`
          : "");
      const doseDisplay =
        dosage.dose ||
        (item.dose
          ? `${item.dose.dsg_time_value} ${item.dose.dsg_inst}`
          : "");
      const routeDisplay = dosage.route_desc || item.ROA_disp;
      const timeInstDisplay =
        dosage.time_instruction_desc || item.time_inst_disp;
      const refillDisplay = dosage.refill || item.refill_count;
      const dosageInstDisplay = dosage.text || item.dosage_instruction;
      const patientInstDisplay = dosage.patient_instruction || item.pat_inst;
      const periodDisplay =
        dosage.period_start && dosage.period_end
          ? `${dosage.period_start} - ${dosage.period_end}`
          : item.duration
            ? `${item.duration.start} - ${item.duration.end}`
            : "";

      const medCode =
        medReq.medicationrequest_code || item.pres_med?.type || "";
      const medDesc =
        medReq.medicationrequest_desc || item.claimMedicalDisplay || "";
      const strength = item.pharstrnth || item.strngth || "";
      const absenceCode = item.pharscabrsn || item.Sntfc_code_abs_rsn || "";
      const absenceOther = item.pharscabrsn_oth || "";
      const selectionReason = item.pharslrsn || item.pselrsn || "";
      const pharmacistSub = item.pharsubs || "";
      const pharmacistSubReason =
        item.pharsubs_oth || item.pharsubsrsn || "";

      return (
        <Col
          xs={12}
          key={`${item.itemcode}-${item.dateserv}-${index}`}
          className="mb-3"
        >
          <Card className="p-3 h-100">
            <Row className="g-3">
              <Col xs={12} md={2} className="text-center">
                <div className="badge badge-subtle-success fs-10 mb-3">
                  {index + 1}
                </div>
                {item.dateserv && (
                  <div className="d-flex justify-content-center mt-2">
                    <Calendar month={month} day={day} year={year} />
                  </div>
                )}
              </Col>
              <Col xs={12} md={10}>
                <Row>
                  <Col
                    xs={12}
                    className="d-flex flex-wrap justify-content-between gap-2 mb-2"
                  >
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <div className="badge badge-subtle-success p-2">
                        {item.itemcode}
                      </div>
                      <div className="fs-10">{item.itemdescp}</div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <div className="badge badge-subtle-warning p-2">
                        {item.itemlclcode}
                      </div>
                      <div className="fs-10">{item.itemlcldescp}</div>
                    </div>
                    {renderStatusBadge(item.serv_status)}
                  </Col>

                  <Col xs={12}>
                    <Row className="gy-2">
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">Type</span>
                          <span className="fs-10 text-600">{item.prdtyp}</span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Unit Price
                          </span>
                          <span className="fs-10 text-600 text-warning">
                            {item.servinfo_prdpri}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Approved Quantity
                          </span>
                          <span className="fs-10 text-600">
                            {item.apprv_qty || 0}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Patient Share
                          </span>
                          <span className="fs-10 text-600 text-success">
                            {item.prdpshre}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Eligible Amount
                          </span>
                          <span className="fs-10 text-600 text-primary">
                            {item.elig_amnt || 0}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Benefit Amount
                          </span>
                          <span className="fs-10 text-600 text-primary">
                            {item.benf_amnt || 0}
                          </span>
                        </div>
                      </Col>
                      {item.invoiceNo && item.invoiceNo !== "-" && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-800 fw-bold">
                              Invoice No
                            </span>
                            <span className="fs-10 text-600 text-info">
                              {item.invoiceNo}
                            </span>
                          </div>
                        </Col>
                      )}
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Tooth No
                          </span>
                          <span className="fs-10 text-600 text-danger">
                            {item.toothno}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Diagnosis
                          </span>
                          <span className="fs-10 text-600">
                            {item?.servinfo_diag && item?.servinfo_diagname
                              ? `(${item?.servinfo_diag}) - ${item?.servinfo_diagname}`
                              : item?.servinfo_diagname ||
                              item?.servinfo_diag ||
                              "-"}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Practitioner
                          </span>
                          <span className="fs-10 text-600">
                            {item.pract_name}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={2}>
                        <div className="d-flex flex-column">
                          <span className="fs-10 text-800 fw-bold">
                            Is Maternity
                          </span>
                          <span className="fs-10 text-600">{item.matrnty}</span>
                        </div>
                      </Col>
                      {item.bodysite && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-800 fw-bold">
                              Body Site
                            </span>
                            <span className="fs-10 text-600">
                              {item.bodysitedesc}
                            </span>
                          </div>
                        </Col>
                      )}
                      {item.subsite && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-800 fw-bold">
                              Sub Site
                            </span>
                            <span className="fs-10 text-600">
                              {item.subsitedesc}
                            </span>
                          </div>
                        </Col>
                      )}
                      {hasPrescriptionDetails && (
                        <div className="fs-9 text-success fw-bold mb-2 border-bottom py-2">
                          Prescription Details
                        </div>
                      )}
                      {medCode && (
                        <Col xs={6} md={2}>
                          <div className="d-flex  flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Prescribed Medication Code
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {medCode}
                            </span>
                          </div>
                        </Col>
                      )}
                      {medDesc && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Prescribed Medication Desc.
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {medDesc}
                            </span>
                          </div>
                        </Col>
                      )}
                      {strength && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Strength
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {strength}
                            </span>
                          </div>
                        </Col>
                      )}
                      {absenceCode && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Absence Reason
                            </span>
                            <span className="fs-10 text-capitalize text-info-emphasis">
                              {absenceCode}
                              {absenceOther && <div>({absenceOther})</div>}
                            </span>
                          </div>
                        </Col>
                      )}

                      {selectionReason && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Selection Reason
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {selectionReason}
                            </span>
                          </div>
                        </Col>
                      )}
                      {pharmacistSub && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Pharmacist Substitute
                            </span>
                            <div className="d-flex flex-column">
                              <span className="fs-10 text-info-emphasis">
                                {pharmacistSub}
                              </span>
                              {pharmacistSubReason && (
                                <span className="fs-10 text-info-emphasis">
                                  ({pharmacistSubReason})
                                </span>
                              )}
                            </div>
                          </div>
                        </Col>
                      )}

                      {periodDisplay && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Period
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {periodDisplay}
                            </span>
                          </div>
                        </Col>
                      )}
                      {durationDisplay && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Duration
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {durationDisplay}
                            </span>
                          </div>
                        </Col>
                      )}
                      {frequencyDisplay && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Frequency
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {frequencyDisplay}
                            </span>
                          </div>
                        </Col>
                      )}
                      {doseDisplay && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Dose
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {doseDisplay}
                            </span>
                          </div>
                        </Col>
                      )}
                      {routeDisplay && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Route
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {routeDisplay}
                            </span>
                          </div>
                        </Col>
                      )}
                      {timeInstDisplay && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Time Instruction
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {timeInstDisplay}
                            </span>
                          </div>
                        </Col>
                      )}
                      {refillDisplay && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Refill
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {refillDisplay}
                            </span>
                          </div>
                        </Col>
                      )}
                      {dosageInstDisplay && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Dosage Instruction
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {dosageInstDisplay}
                            </span>
                          </div>
                        </Col>
                      )}
                      {patientInstDisplay && (
                        <Col xs={6} md={2}>
                          <div className="d-flex flex-column">
                            <span className="fs-10 text-info fw-bold">
                              Patient Instruction
                            </span>
                            <span className="fs-10 text-info-emphasis">
                              {patientInstDisplay}
                            </span>
                          </div>
                        </Col>
                      )}

                      {(item?.disposition || item?.adjreason?.length || item?.reasoncode || item?.process_note?.length > 0 || item?.reasoncode) && (
                        <Col xs={12}>
                          <div className="d-flex flex-column">


                            {item?.disposition && <span className="fs-10 text-danger">
                              {`(Disposition)`} {item?.disposition || ""}
                            </span>}

                            {item?.process_note?.map(item => (
                              <span className="fs-10 text-danger">
                                {`(Process Note)`} {item?.text || ""}
                              </span>
                            ))}

                            {item?.reasoncode && <span className="fs-10 text-danger">
                              {`(Adjustication)`} {`(${item?.reasoncode}) - ${item?.reasondesc}` || ""}
                            </span>}
                            {item?.adjreason?.length && item?.adjreason?.map(item => (
                              <span className="fs-10 text-danger">
                                {`(Adjustication)`} {`[${item?.code}] - ${item?.name}` || ""}
                              </span>
                            ))}

                          </div>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>
        </Col>
      );
    });
  }, [renderStatusBadge, viewClaimInfo, diagnosisList]);

  //   // Render totals section

  const renderTotalsSection = useMemo(() => {
    const services = viewClaimInfo?.service_procedures || [];

    // If no services → don't show the totals card
    if (!services || !Array.isArray(services) || services.length === 0) {
      return null;
    }

    // Safely calculate totals (convert null/undefined/string to number)
    const totalEligible = services.reduce((sum, item) => {
      const amount = parseFloat(item.elig_amnt ?? 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);

    const totalBenefit = services.reduce((sum, item) => {
      const amount = parseFloat(item.benf_amnt ?? 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
    const totalPatientShare = services.reduce((sum, item) => {
      const amount = parseFloat(item.prdpshre ?? 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);

    return (
      <Row className="mt-4">
        <Col xs={12}>
          <Card className="p-3 shadow-md border-0">
            <Row className="g-3">
              <Col xs={12} md={4}>
                <div className="d-flex flex-column">
                  <span className="fs-10 text-800 fw-bold">
                    Eligible Amount
                  </span>
                  <span className="fs-10 fw-bold text-primary">
                    {totalEligible.toFixed(2)} SAR
                  </span>
                </div>
              </Col>

              <Col xs={12} md={4}>
                <div className="d-flex flex-column">
                  <span className="fs-10 text-800 fw-bold">Benefit Amount</span>
                  <span className="fs-10 fw-bold text-primary">
                    {totalBenefit.toFixed(2)} SAR
                  </span>
                </div>
              </Col>

              <Col xs={12} md={4}>
                <div className="d-flex flex-column">
                  <span className="fs-10 text-800 fw-bold">Patient Share</span>
                  <span className="fs-10 fw-bold text-success">
                    {totalPatientShare.toFixed(2)} SAR
                  </span>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>
    );
  }, [viewClaimInfo?.service_procedures]);

  return (
    <>
      <motion.div
        style={{ border: "2px solid #d9f8eb" }}
        className="rounded p-3 position-relative mt-3 mb-3"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.5 }}
      >
        <div className="position-absolute top-0 start-40 translate-middle-y d-none d-md-block">
          <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success px-3 py-2">
            <span className="fs-11">Service & Procedures</span>
          </span>
        </div>

        {renderServiceItems}
        {renderTotalsSection}
      </motion.div>
    </>
  );
};

export default ClaimPayerServices;
